import Axios from 'axios';

// Exportamos la instancia de Axios para que todo el proyecto use la misma
// que tiene los interceptores configurados.
export { Axios };

const TOKEN_KEY = 'store_tid';

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function deleteToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * SECURITY: Elimina las cookies JWT del backend (access_token_cookie,
 * refresh_token_cookie). Son HttpOnly, por lo que no pueden borrarse desde
 * JavaScript con document.cookie; el backend debe expirarlas.
 */
export async function clearAuthCookies() {
  try {
    await Axios.post(
      '/api/v1/autenticacion/logout',
      {},
      { withCredentials: true, timeout: 5000 }
    );
  } catch (error) {
    console.warn('⚠️ [SECURITY] No se pudieron eliminar las cookies de sesión:', error?.message || error);
  }
}

/**
 * SECURITY: Hard Reset - Limpieza profunda de datos de sesión
 * Esta función elimina TODOS los datos sensibles almacenados para prevenir
 * fuga de datos entre usuarios cuando no se hace logout correctamente.
 * 
 * Se ejecuta automáticamente al montar el componente Login para garantizar
 * que cada nuevo inicio de sesión comience con un estado limpio.
 */
export function performHardReset() {
  try {
    // 1. Limpiar todos los tokens y credenciales de localStorage
    const sensitiveKeys = [
      'store_tid',        // Token principal
      'token',            // Token alternativo
      'access_token',     // Token de acceso
      'siesaToken',       // Token de Siesa
      'usuario',          // Datos de usuario
      'user_email',       // Email de usuario
      'debug_logs',       // Logs de debug que pueden contener datos sensibles
      'transactions',     // Transacciones cacheadas
      'axios_401_errors', // Errores que pueden contener tokens
    ];
    
    sensitiveKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // 2. Limpiar sessionStorage completamente
    sessionStorage.clear();
    
    // 3. Limpiar cualquier caché de almacenes o datos de usuario
    // Buscar y eliminar claves que puedan contener datos de usuario
    const allLocalStorageKeys = Object.keys(localStorage);
    allLocalStorageKeys.forEach(key => {
      // Eliminar cualquier clave que pueda contener datos de usuario o caché
      if (key.toLowerCase().includes('usuario') || 
          key.toLowerCase().includes('user') ||
          key.toLowerCase().includes('cache') ||
          key.toLowerCase().includes('almacen') ||
          key.toLowerCase().includes('menu')) {
        localStorage.removeItem(key);
      }
    });
    
    // 4. Limpiar headers por defecto de Axios que puedan tener Authorization heredado
    if (Axios.defaults.headers) {
      delete Axios.defaults.headers.common['Authorization'];
      delete Axios.defaults.headers['Authorization'];
    }
    
    console.log('✅ [SECURITY] Hard Reset completado - Todos los datos sensibles han sido eliminados');
    
    return true;
  } catch (error) {
    console.error('❌ [SECURITY] Error durante Hard Reset:', error);
    return false;
  }
}

// Función para ver logs de debug guardados
export function showDebugLogs() {
  try {
    const logs = JSON.parse(localStorage.getItem('debug_logs') || '[]');
    console.log('📋 LOGS DE DEBUG ALMACENES:');
    logs.forEach((log, idx) => {
      console.log(`[${idx + 1}] ${log.time} - ${log.message}`, log.data || '');
    });
    return logs;
  } catch (e) {
    console.error('Error leyendo logs:', e);
    return [];
  }
}

// Exponer en window para acceso desde consola
if (typeof window !== 'undefined') {
  window.showDebugLogs = showDebugLogs;
}

export function initAxiosInterceptors() {
  // CRÍTICO: Configurar Axios para usar URLs relativas y evitar problemas CORS en Chrome
  Axios.defaults.baseURL = '';
  
  Axios.interceptors.request.use(function(config) {
      const token = getToken();

      // CRÍTICO: NO agregar Authorization a la petición de login
      // Esto previene que tokens heredados de sesiones anteriores se envíen
      const isLoginRequest = config.url && (
        config.url.includes('/autenticacion/iniciar') ||
        config.url.includes('/autenticacion/login')
      );
      
      // Inicializar headers si no existen
      if (!config.headers) {
        config.headers = {};
      }
      
      // SECURITY: Si es una petición de login, forzar la eliminación del header Authorization
      // Esto previene que tokens heredados de sesiones anteriores se envíen accidentalmente
      if (isLoginRequest) {
        delete config.headers['Authorization'];
        return config; // Retornar inmediatamente sin agregar token
      }
      
      // Agregar token a todas las peticiones que contengan /api/ EXCEPTO login
      // Tanto URLs relativas (/api/...) como absolutas (http://localhost:3000/api/...)
      const isApiRequest = config.url && (
        config.url.startsWith('/api/') || 
        config.url.includes('/api/')
      );
      
      if (isApiRequest && token) {
        // Agregar el header de autorización solo si hay token y NO es login
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      
      return config;
  }, function(error) {
    console.error('❌ [INTERCEPTOR] Error en request:', error);
    return Promise.reject(error);
  });

  Axios.interceptors.response.use(
    function(response) {
      return response;
    },
    function(error) {
      // Si es un 401 en una petición a /api/, guardar info y NO redirigir (para debug)
      const isApiError = error.config?.url && (
        error.config.url.startsWith('/api/') || 
        error.config.url.includes('/api/')
      );
      if (isApiError && error.response?.status === 401) {
        // Guardar información del error en sessionStorage para que persista
        const errorInfo = {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
          timestamp: new Date().toISOString(),
          responseStatus: error.response?.status,
          responseData: error.response?.data
        };
        
        // Guardar en sessionStorage (persiste aunque se recargue)
        try {
          const existingErrors = JSON.parse(sessionStorage.getItem('axios_401_errors') || '[]');
          existingErrors.push(errorInfo);
          sessionStorage.setItem('axios_401_errors', JSON.stringify(existingErrors));
        } catch (e) {
          console.error('Error guardando en sessionStorage:', e);
        }
        
        // NO REDIRIGIR - dejar que el componente maneje el error
        // deleteToken();
        // window.location = '/login';
      }
      
      return Promise.reject(error);
    }
  );
}
